/**
 * ============================================================================
 * LocaleManagementPage — 지원 언어 레지스트리 CRUD (cm_locale)
 * ============================================================================
 *
 * 【역할】
 *   시스템이 지원하는 언어(locale) 를 추가/수정/삭제.
 *   신규 언어 행 INSERT 만으로 메뉴/라벨/메시지/헤더 드롭다운이 자동 인식
 *   (Redis pub/sub 'locales' 채널 invalidate → 모든 인스턴스 캐시 재로드).
 *
 * 【주요 기능】
 *   - 검색: keyword (코드/영문명/자국어 부분 일치)
 *   - CRUD: 추가(빈 행, sortOrder 자동 +10) / 인라인 수정 / 삭제 / 벌크 저장
 *   - 정합성: 기본 언어(isDefault=1) 단일성 서버 보장 — 프론트는 한 행만 Y
 *   - cascade: 삭제 시 서버가 cm_menu_i18n/label_i18n/message_i18n/code_i18n 자동 처리
 *   - 정규화: 0/1 ↔ Y/N 양방향 변환 (toYN, toBit 헬퍼)
 *
 * 【Backend 호출】
 *   - POST /api/v1/transaction (service=locale/searchList) — 언어 목록 (비활성 포함)
 *   - POST /api/v1/transaction (service=locale/save)       — 벌크 저장 + pub/sub invalidate
 *
 * 【DataSet/Hook】
 *   - useDataSet<LocaleRow>() — ds_localeList : 언어 목록 (PK: localeCode)
 *   - useTransaction()        — RPC 통합 호출
 *   - useLabel()              — i18n 라벨(t)
 *
 * 【사용처】 router/index.tsx — `{ path: '/admin/locale', element: <LocaleManagementPage /> }`
 * 【포팅 원본】 react-spring-fw `frontend/src/pages/LocaleManagementPage.tsx`
 * 【의존성】 antd Form/Input/InputNumber/Select/Tag, framework hooks
 */
import { useEffect, useMemo, useState } from 'react'
import { Form, Input, InputNumber, Select, Tag } from 'antd'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'
import FormInput from '@/components/common/FormInput'
import { useDataSet, useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import { makeColumns, type ColumnSpec } from '@/framework/grid/makeColumns'
import type { TrackedRow } from '@/framework/useDataSet'

/** 언어 1행 — cm_locale 매핑. */
interface LocaleRow {
  /** 언어 코드 PK (소문자, 예: "ko"·"en"·"zh"·"ja"·"vi"). */
  localeCode: string
  /** 영문 표기 (예: "Korean", "English"). */
  localeName?: string
  /** 자국어 표기 (예: "한국어", "English", "中文"). */
  nativeName?: string
  /** 국기 이모지 (예: "🇰🇷"). */
  flagEmoji?: string
  /** 정렬 순서 (오름차순). 추가 시 max+10 자동 계산. */
  sortOrder?: number
  /** 0 | 1 — 서버 is_default 와 대응. 'Y'|'N' 도 수용. 단일성은 서버 보장. */
  isDefault?: number | 'Y' | 'N'
  /** 0 | 1 — 활성 여부. 서버 is_active 와 대응. */
  isActive?: number | 'Y' | 'N'
}

/** isDefault/isActive 값 정규화 (서버 Number(0/1) ↔ UI 'Y'/'N'). */
const toYN = (v: unknown): 'Y' | 'N' => {
  if (v === 1 || v === '1' || v === true || v === 'Y' || v === 'y') return 'Y'
  return 'N'
}
const toBit = (yn: 'Y' | 'N'): number => (yn === 'Y' ? 1 : 0)

/**
 * LocaleManagementPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function LocaleManagementPage() {
  const ds = useDataSet<LocaleRow>()
  const { transaction } = useTransaction()
  const { t } = useLabel()
  const [searchForm] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)

  /** 언어 조회 — locale/searchList (비활성 포함). */
  const search = async (values?: Record<string, unknown>) => {
    setIsLoading(true)
    try {
      await transaction({
        id: 'select',
        service: 'locale/searchList',
        inds: { ds_search: values ?? searchForm.getFieldsValue() },
        outds: { ds_localeList: ds },
        loading: false
      })
    } finally {
      setIsLoading(false)
    }
  }

  // 마운트 시 1회 자동 조회
  useEffect(() => {
    search()
  }, [])

  /** [추가] — 빈 행 (isActive=1, isDefault=0, sortOrder=(max+10)). */
  const add = () => {
    const maxSort = ds.rows.reduce((m, r) => Math.max(m, Number(r.data.sortOrder) || 0), 0)
    ds.addRow({
      localeCode: '',
      localeName: '',
      nativeName: '',
      flagEmoji: '',
      sortOrder: maxSort + 10,
      isDefault: 0,
      isActive: 1
    } as LocaleRow)
  }

  /** [삭제] — 선택 행 _state='D' (서버가 is_default=1 차단). */
  const del = () => ds.selectedIndex >= 0 && ds.deleteRow(ds.selectedIndex)

  /** [저장] — locale/save 호출 후 목록 재조회.
   *  서버 후속 작업: is_default 단일성 + cascade + Redis pub/sub invalidate.
   *  로컬 localeStore 리로드는 pub/sub 수신측에서 자동 처리됨. */
  const save = async () => {
    setIsLoading(true)
    try {
      const res = await transaction({
        id: 'save',
        service: 'locale/save',
        action: 'save',
        inds: { ds_localeList: ds },
        outds: { ds_localeList: ds },
        loading: false
      })
      if (res.success) await search()
    } finally {
      setIsLoading(false)
    }
  }

  const patch = (idx: number, key: keyof LocaleRow, v: unknown) =>
    ds.updateRow(idx, { [key]: v } as Partial<LocaleRow>)

  const columns = useMemo(() => {
    const specs: ColumnSpec<TrackedRow<LocaleRow>>[] = [
      {
        key: 'COL_LOCALE_CODE',
        dataIndex: ['data', 'localeCode'],
        width: 120,
        render: (_v, r, i) => (
          <Input
            size="small"
            value={r.data.localeCode}
            disabled={r._state !== 'C'}
            placeholder="ko, en, zh, ja, vi..."
            onChange={(e) => patch(i, 'localeCode', e.target.value.trim())}
          />
        )
      },
      {
        key: 'COL_LOCALE_NAME',
        dataIndex: ['data', 'localeName'],
        width: 160,
        render: (_v, r, i) => (
          <Input
            size="small"
            value={r.data.localeName}
            placeholder="Korean, English..."
            onChange={(e) => patch(i, 'localeName', e.target.value)}
          />
        )
      },
      {
        key: 'COL_NATIVE_NAME',
        dataIndex: ['data', 'nativeName'],
        width: 160,
        render: (_v, r, i) => (
          <Input
            size="small"
            value={r.data.nativeName}
            placeholder={t('PHR_LOCALE_NATIVE_NAME', '한국어, English, 中文...')}
            onChange={(e) => patch(i, 'nativeName', e.target.value)}
          />
        )
      },
      {
        key: 'COL_FLAG_EMOJI',
        dataIndex: ['data', 'flagEmoji'],
        width: 90,
        render: (_v, r, i) => (
          <Input
            size="small"
            value={r.data.flagEmoji}
            placeholder="🇰🇷"
            onChange={(e) => patch(i, 'flagEmoji', e.target.value)}
          />
        )
      },
      {
        key: 'COL_SORT_ORDER',
        dataIndex: ['data', 'sortOrder'],
        width: 90,
        render: (_v, r, i) => (
          <InputNumber
            size="small"
            style={{ width: '100%' }}
            value={r.data.sortOrder}
            onChange={(v) => patch(i, 'sortOrder', v)}
          />
        )
      },
      {
        key: 'COL_IS_DEFAULT',
        dataIndex: ['data', 'isDefault'],
        width: 100,
        render: (_v, r, i) => (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={toYN(r.data.isDefault)}
            options={[
              { label: t('LBL_DEFAULT', '기본'), value: 'Y' },
              { label: '-', value: 'N' }
            ]}
            onChange={(v) => patch(i, 'isDefault', toBit(v as 'Y' | 'N'))}
          />
        )
      },
      {
        key: 'COL_IS_ACTIVE',
        dataIndex: ['data', 'isActive'],
        width: 90,
        render: (_v, r, i) => (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={toYN(r.data.isActive)}
            options={[
              { label: t('LBL_YES', '사용'), value: 'Y' },
              { label: t('LBL_NO', '미사용'), value: 'N' }
            ]}
            onChange={(v) => patch(i, 'isActive', toBit(v as 'Y' | 'N'))}
          />
        )
      },
      {
        key: 'COL_STATUS',
        dataIndex: '_state',
        width: 70,
        render: (_v, r) => {
          if (r._state === 'C') return <Tag color="green">{t('TTL_ADD', '신규')}</Tag>
          if (r._state === 'U') return <Tag color="orange">{t('TTL_EDIT', '수정')}</Tag>
          return (
            <Tag color={toYN(r.data?.isActive) === 'Y' ? 'blue' : 'default'}>
              {toYN(r.data?.isActive) === 'Y'
                ? t('LBL_ACTIVE', '활성')
                : t('LBL_INACTIVE', '비활성')}
            </Tag>
          )
        }
      }
    ]
    return makeColumns(specs, t)
  }, [t, ds])

  return (
    <AppPage
      search={{
        form: searchForm,
        onSearch: () => search(),
        content: (
          <Form.Item label={t('LBL_KEYWORD', '검색어')} name="keyword">
            <FormInput placeholder={t('LBL_LOCALE_SEARCH_PLACEHOLDER', '코드/영문명/자국어')} />
          </Form.Item>
        )
      }}
      toolbar={{
        onSearch: () => search(),
        onAdd: add,
        onDelete: del,
        onSave: save,
        dirty: ds.isDirty
      }}
    >
      <DataTable<TrackedRow<LocaleRow>>
        rowKey="_rowId"
        dataSource={ds.getVisibleRows()}
        columns={columns}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: ds.selectedRow ? [ds.selectedRow._rowId] : [],
          onChange: (keys) => {
            const idx = ds.rows.findIndex((r) => r._rowId === keys[0])
            ds.setSelectedIndex(idx)
          }
        }}
      />
    </AppPage>
  )
}
